import { useEffect, useState } from "react";
import AdminLayout from "./components/AdminLayout";
import { supabase } from "@/lib/supabase";
import type { Category } from "@/lib/supabase";

export default function CategoriasAdminPage() {
  const [items, setItems] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Partial<Category> | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const fetch = async () => {
    setLoading(true);
    const { data } = await supabase.from("categories").select("*").order("name");
    setItems(data ?? []);
    setLoading(false);
  };

  useEffect(() => { fetch(); }, []);

  const openNew = () => { setEditing({ name: "", slug: "" }); setShowModal(true); };
  const openEdit = (c: Category) => { setEditing(c); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditing(null); };

  const handleSave = async () => {
    if (!editing?.name) return;
    setSaving(true);
    const slug = editing.slug || editing.name.toLowerCase().replace(/\s+/g, "-");
    if (editing.id) {
      await supabase.from("categories").update({ name: editing.name, slug }).eq("id", editing.id);
    } else {
      await supabase.from("categories").insert({ name: editing.name, slug });
    }
    setSaving(false);
    closeModal();
    fetch();
  };

  const handleDelete = async (id: string) => {
    await supabase.from("categories").delete().eq("id", id);
    setDeleteId(null);
    fetch();
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a1a]">Categorias</h1>
          <p className="text-gray-500 text-sm mt-1">{items.length} categoria(s)</p>
        </div>
        <button onClick={openNew} className="flex items-center gap-2 bg-[#1B1F5E] hover:bg-[#2F4F8F] text-white font-bold text-sm px-4 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></div>
          Nova Categoria
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-6 h-6 flex items-center justify-center text-[#1B1F5E]"><i className="ri-loader-4-line animate-spin text-2xl"></i></div>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3"><i className="ri-price-tag-3-line text-4xl"></i></div>
            <p className="text-sm">Nenhuma categoria cadastrada</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Nome</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Slug</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Criado em</th>
                <th className="text-center text-xs font-semibold text-gray-500 px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {items.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-[#1a1a1a]">{c.name}</td>
                  <td className="px-4 py-3"><span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded font-mono">{c.slug}</span></td>
                  <td className="px-4 py-3 text-sm text-gray-500">{new Date(c.created_at).toLocaleDateString("pt-BR")}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => openEdit(c)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-[#1B1F5E] transition-colors cursor-pointer"><i className="ri-edit-line text-sm"></i></button>
                      <button onClick={() => setDeleteId(c.id)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors cursor-pointer"><i className="ri-delete-bin-line text-sm"></i></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-[#1a1a1a]">{editing.id ? "Editar Categoria" : "Nova Categoria"}</h2>
              <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-xl"></i></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#333] mb-1.5">Nome *</label>
                <input value={editing.name ?? ""} onChange={e => setEditing({ ...editing, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1B1F5E]" placeholder="Nome da categoria" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#333] mb-1.5">Slug</label>
                <input value={editing.slug ?? ""} onChange={e => setEditing({ ...editing, slug: e.target.value })} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1B1F5E] font-mono" placeholder="slug-da-categoria" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={closeModal} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 bg-[#1B1F5E] hover:bg-[#2F4F8F] text-white font-bold text-sm rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-60">{saving ? "Salvando..." : "Salvar"}</button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4"><i className="ri-delete-bin-line text-red-500 text-xl"></i></div>
            <h3 className="text-base font-bold text-[#1a1a1a] mb-2">Excluir categoria?</h3>
            <p className="text-gray-500 text-sm mb-6">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white font-bold text-sm rounded-lg cursor-pointer whitespace-nowrap">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
